import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, User, CheckSquare, Square } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface MatchResult {
	id: string;
	name: string;
	entity_type: string;
	score: number;
	scorePercent: number;
	matchId?: string; // Optional property to indicate if already matched
}

interface MatchResultsListProps {
	results: Record<string, MatchResult>;
	isLoading: boolean;
	scoreThreshold: number;
	jobId: string | undefined;
	onAddSelected?: (candidates: string[]) => void;
	onAddAll?: () => void;
}

const getScoreColor = (score: number) => {
	if (score <= 10) {
		return "bg-red-500";
	} else if (score <= 50) {
		return "bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500";
	} else {
		return "bg-gradient-to-r from-yellow-500 via-yellow-300 to-green-500";
	}
};

export const MatchResultsList = ({ 
	results, 
	isLoading, 
	scoreThreshold, 
	jobId,
	onAddSelected,
	onAddAll 
}: MatchResultsListProps) => {
	const [candidateSummaries, setCandidateSummaries] = useState<Record<string, string>>({});
	const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
	const [displayedResults, setDisplayedResults] = useState<MatchResult[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const [addingCandidates, setAddingCandidates] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const initialLoadDone = useRef(false);
	const loadMoreThreshold = 200; // pixels from bottom to trigger load more
	const resultsPerBatch = 5;
	
	const filteredResults = Object.values(results)
		.filter(result => result.scorePercent * 100 >= scoreThreshold)
		.sort((a, b) => b.scorePercent - a.scorePercent);
	
	useEffect(() => {
		if (filteredResults.length > 0 && !initialLoadDone.current) {
			const initialBatch = filteredResults.slice(0, resultsPerBatch);
			setDisplayedResults(initialBatch);
			setHasMore(filteredResults.length > resultsPerBatch);
			initialLoadDone.current = true;
		} else if (filteredResults.length === 0) {
			setDisplayedResults([]);
			setHasMore(false);
		}
	}, [filteredResults]);
	
	useEffect(() => {
		initialLoadDone.current = false;
		const initialBatch = filteredResults.slice(0, resultsPerBatch);
		setDisplayedResults(initialBatch);
		setHasMore(filteredResults.length > resultsPerBatch);
		setSelectedCandidates(new Set()); // Reset selection when threshold changes
	}, [scoreThreshold]);
	
	const loadMoreResults = useCallback(() => {
		if (loading || !hasMore) return;
		
		setLoading(true);
		
		setTimeout(() => {
			const nextBatch = filteredResults.slice(
				displayedResults.length,
				displayedResults.length + resultsPerBatch
			);
			
			if (nextBatch.length === 0) {
				setHasMore(false);
			} else {
				setDisplayedResults(prev => [...prev, ...nextBatch]);
				setHasMore(displayedResults.length + nextBatch.length < filteredResults.length);
			}
			
			setLoading(false);
		}, 300);
	}, [filteredResults, displayedResults, loading, hasMore]);
	
	const handleScroll = useCallback(() => {
		if (!scrollContainerRef.current) return;
		
		const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
		
		if (scrollHeight - scrollTop - clientHeight < loadMoreThreshold) {
			loadMoreResults();
		}
	}, [loadMoreResults]);
	
	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
			return () => scrollContainer.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll]);
	
	useEffect(() => {
		const fetchCandidateSummaries = async () => {
			const candidateIds = displayedResults.map(result => result.id);
			if (candidateIds.length === 0) return;
			
			const { data, error } = await supabase
				.from('candidates')
				.select('id, ai_synthesis')
				.in('id', candidateIds);
			
			if (error) {
				console.error('Error fetching candidate summaries:', error);
				return;
			}
			
			const summaries: Record<string, string> = {};
			data.forEach(candidate => {
				summaries[candidate.id] = candidate.ai_synthesis || '';
			});
			setCandidateSummaries(prevSummaries => ({ ...prevSummaries, ...summaries }));
		};
		
		fetchCandidateSummaries();
	}, [displayedResults]);
	
	const toggleCandidateSelection = (candidateId: string) => {
		setSelectedCandidates(prev => {
			const newSet = new Set(prev);
			if (newSet.has(candidateId)) {
				newSet.delete(candidateId);
			} else {
				newSet.add(candidateId);
			}
			return newSet;
		});
	};

	const handleAddSelected = async () => {
		if (selectedCandidates.size === 0) {
			toast({
				title: "No candidates selected",
				description: "Please select at least one candidate.",
				variant: "destructive"
			});
			return;
		}

		if (!jobId) {
			toast({
				title: "Error",
				description: "No job selected",
				variant: "destructive"
			});
			return;
		}

		try {
			setAddingCandidates(true);
			const selectedCandidateIds = Array.from(selectedCandidates);
			const matchInserts = selectedCandidateIds.map(candidateId => ({
				job_id: jobId,
				candidate_id: candidateId,
				score: 0, // Initial score
				status: 'matched'
			}));

			// First check if matches already exist to avoid the conflict error
			const { data: existingMatches, error: checkError } = await supabase
				.from('matches')
				.select('candidate_id')
				.eq('job_id', jobId)
				.in('candidate_id', selectedCandidateIds);

			if (checkError) {
				throw checkError;
			}

			// Filter out candidates that already have matches
			const existingIds = new Set(existingMatches?.map(match => match.candidate_id) || []);
			const newMatchInserts = matchInserts.filter(match => !existingIds.has(match.candidate_id));

			if (newMatchInserts.length === 0) {
				toast({
					title: "Info",
					description: "All selected candidates are already matched to this job."
				});
				setAddingCandidates(false);
				return;
			}

			// Insert only new matches
			const { error } = await supabase
				.from('matches')
				.insert(newMatchInserts);

			if (error) throw error;

			toast({
				title: "Success",
				description: `Added ${newMatchInserts.length} candidate(s) to job.`
			});

			setSelectedCandidates(new Set());
			onAddSelected?.(selectedCandidateIds);
		} catch (error) {
			console.error('Error adding matches:', error);
			toast({
				title: "Error",
				description: "Failed to add candidates to job",
				variant: "destructive"
			});
		} finally {
			setAddingCandidates(false);
			location.href = location.href; // Refresh the page to reflect changes
		}
	};

	const handleAddAll = async () => {
		if (!jobId) {
			toast({
				title: "Error",
				description: "No job selected",
				variant: "destructive"
			});
			return;
		}

		try {
			setAddingCandidates(true);
			// Only use the filtered visible results that meet the score threshold
			const allCandidateIds = filteredResults.map(result => result.id);
			
			if (allCandidateIds.length === 0) {
				toast({
					title: "Info",
					description: "No candidates meet the current filter criteria."
				});
				setAddingCandidates(false);
				return;
			}
			
			// First check if matches already exist to avoid the conflict error
			const { data: existingMatches, error: checkError } = await supabase
				.from('matches')
				.select('candidate_id')
				.eq('job_id', jobId)
				.in('candidate_id', allCandidateIds);

			if (checkError) {
				throw checkError;
			}

			// Filter out candidates that already have matches
			const existingIds = new Set(existingMatches?.map(match => match.candidate_id) || []);
			const newMatchInserts = allCandidateIds
				.filter(id => !existingIds.has(id))
				.map(candidateId => ({
					job_id: jobId,
					candidate_id: candidateId,
					score: 0,
					status: 'matched'
				}));

			if (newMatchInserts.length === 0) {
				toast({
					title: "Info",
					description: "All candidates are already matched to this job."
				});
				setAddingCandidates(false);
				return;
			}

			// Insert only new matches
			const { error } = await supabase
				.from('matches')
				.insert(newMatchInserts);

			if (error) throw error;

			toast({
				title: "Success",
				description: `Added ${newMatchInserts.length} candidate(s) to job.`
			});

			onAddAll?.();
		} catch (error) {
			console.error('Error adding matches:', error);
			toast({
				title: "Error",
				description: "Failed to add candidates to job",
				variant: "destructive"
			});
		} finally {
			setAddingCandidates(false);
		}
	};
	
	if (isLoading) {
		return (
			<div className="flex justify-center items-center p-8">
				<Loader2 className="h-8 w-8 animate-spin text-gray-500" />
			</div>
		);
	}
	
	if (!results || Object.keys(results).length === 0) {
		return (
			<div className="text-center p-8 text-gray-500">
				No matches found. Try adjusting your search criteria.
			</div>
		);
	}
	
	return (
		<div className="flex flex-col h-full relative">
			<div 
				ref={scrollContainerRef}
				className="space-y-4 overflow-y-auto pr-2"
				style={{ maxHeight: 'calc(100vh - 300px)', paddingBottom: '70px' }}
			>
				{displayedResults.map((result) => (
					<Card key={result.id} className="p-4 hover:bg-gray-50 transition-colors">
						<div className="flex items-start gap-4">
							<div 
								className={`${result.matchId ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
								onClick={() => {
									if (!result.matchId) {
										toggleCandidateSelection(result.id);
									}
								}}
							>
								{selectedCandidates.has(result.id) ? (
									<CheckSquare className="h-5 w-5 text-blue-600" />
								) : (
									<Square className="h-5 w-5 text-gray-400" />
								)}
							</div>
							<div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
								<User className="h-5 w-5 text-blue-600" />
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex justify-between items-start mb-2">
									<div className="flex items-center gap-2">
										<h3 className="font-medium truncate">{result.name}</h3>
										{result.matchId && (
											<span className="text-xs text-gray-500 italic">Already matched</span>
										)}
									</div>
									<span className="text-sm font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
										{Math.round(result.scorePercent * 100)}%
									</span>
								</div>
								<Progress 
									value={result.scorePercent * 100} 
									className="h-2"
								>
									<div 
										className={`h-full absolute top-0 left-0 ${getScoreColor(result.scorePercent * 100)}`}
										style={{ width: `${result.scorePercent * 100}%` }}
									/>
								</Progress>
								<div className="mt-2 text-sm text-gray-600">
									{candidateSummaries[result.id] || 'Loading summary...'}
								</div>
							</div>
						</div>
					</Card>
				))}
				
				{loading && (
					<div className="flex justify-center items-center py-4">
						<Loader2 className="h-6 w-6 animate-spin text-gray-500" />
					</div>
				)}
				
				{!loading && !hasMore && displayedResults.length > 0 && (
					<div className="text-center py-4 text-gray-500 text-sm">
						No more results to load
					</div>
				)}
			</div>
			
			<div className="fixed bottom-0 left-0 right-0 flex justify-between items-center py-4 px-6 bg-white border-t shadow-md z-10">
				<div className="text-sm text-gray-500">
					Showing {displayedResults.length} of {filteredResults.length} matches
				</div>
				<div className="flex gap-2">
					<Button 
						variant="secondary" 
						onClick={handleAddSelected} 
						disabled={selectedCandidates.size === 0 || addingCandidates}
					>
						{addingCandidates ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Adding...
							</>
						) : (
							`Add Selected (${selectedCandidates.size})`
						)}
					</Button>
					<Button 
						onClick={handleAddAll}
						disabled={filteredResults.length === 0 || addingCandidates}
					>
						{addingCandidates ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Adding...
							</>
						) : (
							"Add All"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};
